import { useState, useEffect } from 'react'
import api from '../services/api'

// Tabela de Ocorrências (TOCORREN, frota.ocorre) — tipos usados nas ocorrências do funcionário

interface Ocorrencia { ocrcod: number; ocrnom: string; ocresc: string; ocrger: string; ocrcmp: string; usos?: number }

// "Documentos adicionais": no banco só existe Nenhum (em branco); demais opções do ERP ainda não mapeadas
const DOCUMENTOS: [string, string][] = [[' ', 'Nenhum']]
const simNao = (v: string) => (v === 'S' ? 'Sim' : 'Não')

function Lista({ lista, onAdicionar, onEditar, onExcluir }: {
  lista: Ocorrencia[]
  onAdicionar: () => void
  onEditar: (o: Ocorrencia) => void
  onExcluir: (o: Ocorrencia) => void
}) {
  const [busca, setBusca] = useState('')
  const t = busca.trim().toUpperCase()
  const filtrada = t ? lista.filter(o => String(o.ocrcod).includes(t) || o.ocrnom.includes(t)) : lista
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: 10, flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Ocorrências cadastradas ({lista.length})</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="field" style={{ margin: 0 }}>
            <input value={busca} placeholder="Buscar código ou descrição" style={{ width: 240 }} onChange={e => setBusca(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={onAdicionar}>+ Adicionar</button>
        </div>
      </div>

      {filtrada.length === 0 && <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Nenhuma ocorrência encontrada.</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {filtrada.map(o => (
          <div key={o.ocrcod} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{o.ocrcod} - {o.ocrnom || <span style={{ color: '#9ca3af', fontWeight: 400 }}>(sem descrição)</span>}</div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>Escala: <b>{simNao(o.ocresc)}</b></span>
                <span>Gera autom.: <b>{simNao(o.ocrger)}</b></span>
                <span>Lançamentos: <b>{o.usos ?? 0}</b></span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-outline btn-sm" onClick={() => onEditar(o)}>Editar</button>
              <button className="btn btn-outline btn-sm" style={{ color: '#b91c1c' }} onClick={() => onExcluir(o)}
                disabled={(o.usos ?? 0) > 0} title={(o.usos ?? 0) > 0 ? 'Ocorrência já lançada em funcionários' : undefined}>Excluir</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Formulario({ editando, inicial, onVoltar, onSalvo }: {
  editando: boolean
  inicial: Ocorrencia
  onVoltar: () => void
  onSalvo: () => void
}) {
  const [form, setForm] = useState<Ocorrencia>(inicial)
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)
  const opcoesDoc = DOCUMENTOS.some(([v]) => v === form.ocrcmp) ? DOCUMENTOS : [...DOCUMENTOS, [form.ocrcmp, form.ocrcmp] as [string, string]]

  async function salvar() {
    if (!form.ocrcod || form.ocrcod <= 0) { setErro('Codigo inválido da ocorrência!'); return }
    setSalvando(true); setErro('')
    try {
      if (editando) await api.put(`/ocorrencias/${form.ocrcod}`, form)
      else await api.post('/ocorrencias', form)
      onSalvo()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando(false)
    }
  }

  const sn = (campo: 'ocresc' | 'ocrger', label: string) => (
    <div className="field">
      <label>{label}</label>
      <select value={form[campo]} onChange={e => setForm(f => ({ ...f, [campo]: e.target.value }))}>
        <option value="N">Não</option>
        <option value="S">Sim</option>
      </select>
    </div>
  )

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>{editando ? 'Editar Ocorrência' : 'Nova Ocorrência'}</h3>
        <button className="btn btn-outline" onClick={onVoltar}>Voltar</button>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field">
          <label>Código *</label>
          <input type="number" value={form.ocrcod || ''} disabled={editando} style={{ width: '10ch' }}
            onChange={e => setForm(f => ({ ...f, ocrcod: Number(e.target.value) }))} />
        </div>
        <div className="field">
          <label>Descrição</label>
          <input value={form.ocrnom} maxLength={60} style={{ width: '62ch', maxWidth: '100%' }}
            onChange={e => setForm(f => ({ ...f, ocrnom: e.target.value.toUpperCase() }))} />
        </div>
      </div>

      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '1.1rem 0 0.5rem' }}>
        Terminal do funcionário
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {sn('ocresc', 'Utilizada na escala?')}
        {sn('ocrger', 'Gera ocorrência autom.')}
        <div className="field">
          <label>Documentos adicionais</label>
          <select value={form.ocrcmp} onChange={e => setForm(f => ({ ...f, ocrcmp: e.target.value }))}>
            {opcoesDoc.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.75rem' }}>{erro}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Adicionar'}
        </button>
        <button className="btn btn-outline" onClick={onVoltar}>Cancelar</button>
      </div>
    </div>
  )
}

export default function Ocorrencias() {
  const [lista, setLista] = useState<Ocorrencia[]>([])
  const [carregado, setCarregado] = useState(false)
  const [atual, setAtual] = useState<Ocorrencia | null>(null)
  const [editando, setEditando] = useState(false)
  const [erro, setErro] = useState('')

  async function carregar() {
    const r = await api.get('/ocorrencias')
    setLista(r.data)
    setCarregado(true)
  }

  useEffect(() => { carregar().catch(() => setErro('Erro ao carregar as ocorrências.')) }, [])

  async function excluir(o: Ocorrencia) {
    if (!window.confirm(`Excluir a ocorrência "${o.ocrcod} - ${o.ocrnom}"?`)) return
    setErro('')
    try {
      await api.delete(`/ocorrencias/${o.ocrcod}`)
      await carregar()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao excluir.')
    }
  }

  return (
    <div style={{ maxWidth: 860 }}>
      <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        Ocorrências
        <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 400 }} title="Tabela frota.ocorre">TOCORREN</span>
      </h2>
      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{erro}</div>}

      {!carregado ? null : atual ? (
        <Formulario key={atual.ocrcod || 'nova'} editando={editando} inicial={atual}
          onVoltar={() => setAtual(null)} onSalvo={async () => { await carregar(); setAtual(null) }} />
      ) : (
        <Lista lista={lista}
          onAdicionar={() => { setAtual({ ocrcod: 0, ocrnom: '', ocresc: 'N', ocrger: 'N', ocrcmp: ' ' }); setEditando(false) }}
          onEditar={o => { setAtual({ ...o }); setEditando(true) }}
          onExcluir={excluir} />
      )}
    </div>
  )
}
